import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Box, LinearProgress } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CRM_POLL, crmApi } from '../../../features/crm/crmApi'
import { Card, CardHead, Hint, Kpis, Muted, PageHead, PeriodBar, Pill, PrimaryButton, TableWrap, contentSx, pageSx, usePeriod } from '../shared/crmUi'
import { amoId, dayKeyOf, dictLabel, fmtDate, hm, money, subjectsLine } from '../shared/format'
import { useCrmDict } from '../shared/dict'
import { sellerStage } from '../shared/stages'
import { EmptyRow } from '../head/HeadCrmNewPage'

const MyIntakesPage = () => {
    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const period = usePeriod('month')
    const { data = [], isFetching } = crmApi.useListIntakesQuery(
        { tab: 'MY', from: period.from, to: period.to },
        { pollingInterval: CRM_POLL },
    )

    // платежи рассрочки - отдельные строки «N-ай төлемі» по дате платежа (заявка могла быть раньше периода)
    const { data: inst = [] } = crmApi.useListInstallmentsQuery('SELLER', { pollingInterval: CRM_POLL })
    const dict = useCrmDict()

    const rows = useMemo(() => [...data].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)), [data])
    const pays = useMemo(
        () =>
            inst
                .flatMap((x) => (x.payments ?? []).map((p) => ({ p, x })))
                .filter(({ p }) => p.monthNo > 1 && dayKeyOf(p.createdAt) >= period.from && dayKeyOf(p.createdAt) <= period.to),
        [inst, period.from, period.to],
    )
    const lines = useMemo(
        () =>
            [...rows.map((r) => ({ at: r.createdAt, r, pay: null as null | (typeof pays)[number] })), ...pays.map((pay) => ({ at: pay.p.createdAt, r: pay.x, pay }))].sort(
                (a, b) => (a.at < b.at ? 1 : -1),
            ),
        [rows, pays],
    )
    const active = rows.filter((r) => r.status !== 'RETURNED')
    const paySum = pays.reduce((s, { p }) => s + (p.amount || 0), 0)
    const firstSum = active.reduce((s, r) => s + (r.data?.paidSum || 0), 0)
    const turnover = firstSum + paySum
    const books = active.reduce((s, r) => s + (r.data?.bookSum || 0), 0)
    const discounts = active.reduce((s, r) => s + (r.data?.discountSum || 0), 0)
    const returns = rows.length - active.length

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead
                    title={formatMessage({ id: 'crmMyStudents' })}
                    sub={formatMessage({ id: 'crmMyStudentsSub' })}
                    action={<PrimaryButton onClick={() => navigate('/crm/register')}>＋ {formatMessage({ id: 'crmRegisterTitle' })}</PrimaryButton>}
                />

                <Card sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <PeriodBar period={period} />
                    <Kpis
                        items={[
                            { label: formatMessage({ id: 'crmKpiStudents' }), value: active.length },
                            {
                                label: formatMessage({ id: 'crmKpiTurnover' }),
                                value: money(turnover),
                                sub: pays.length ? formatMessage({ id: 'crmMonthlyInside' }, { n: pays.length, sum: money(paySum) }) : undefined,
                            },
                            { label: formatMessage({ id: 'crmKpiAvg' }), value: money(active.length ? Math.round(firstSum / active.length) : 0) },
                            { label: formatMessage({ id: 'crmKpiBooks' }), value: money(books) },
                            { label: formatMessage({ id: 'crmKpiDiscount' }), value: money(discounts) },
                            { label: formatMessage({ id: 'crmKpiReturns' }), value: returns, bad: returns > 0 },
                        ]}
                    />
                    <Hint>{formatMessage({ id: 'crmTurnoverHint' }, { from: fmtDate(period.from), to: fmtDate(period.to) })}</Hint>
                </Card>

                <Card>
                    <CardHead title={formatMessage({ id: 'crmStudents' })} right={formatMessage({ id: 'crmRecords' }, { count: lines.length })} />
                    {isFetching && <LinearProgress sx={{ marginBottom: 1 }} />}
                    <TableWrap minWidth={820}>
                        <table>
                            <thead>
                                <tr>
                                    <th>{formatMessage({ id: 'crmColDate' })}</th>
                                    <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                    <th>{formatMessage({ id: 'crmColSubjects' })}</th>
                                    <th>{formatMessage({ id: 'crmColPackage' })}</th>
                                    <th className="num">{formatMessage({ id: 'crmColSum' })}</th>
                                    <th>{formatMessage({ id: 'crmColStage' })}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map(({ r, pay }) => {
                                    if (pay)
                                        return (
                                            <tr key={`p${pay.p.id}`}>
                                                <td className="num">
                                                    {fmtDate(pay.p.createdAt)}
                                                    <Muted>{hm(pay.p.createdAt)}</Muted>
                                                </td>
                                                <td>
                                                    <b>{r.data?.studentName}</b>
                                                    <Muted>{formatMessage({ id: 'crmMonthlyPayment' })}</Muted>
                                                </td>
                                                <td>{formatMessage({ id: 'crmMonthN' }, { n: pay.p.monthNo })}</td>
                                                <td>{dict.label('PACKAGE', r.data?.packageCode)}</td>
                                                <td className="num">{money(pay.p.amount)}</td>
                                                <td>
                                                    <Pill tone={pay.p.status === 'CONFIRMED' ? 'ok' : 'blue'}>
                                                        {formatMessage({ id: 'crmMonthN' }, { n: pay.p.monthNo })}
                                                        {pay.p.status === 'CONFIRMED' ? ' ✓' : ''}
                                                    </Pill>
                                                </td>
                                                <td />
                                            </tr>
                                        )
                                    const st = sellerStage(formatMessage, r)
                                    return (
                                        <tr key={r.id} className={r.status === 'RETURNED' ? 'ret' : ''}>
                                            <td className="num">
                                                {fmtDate(r.createdAt)}
                                                <Muted>{hm(r.createdAt)}</Muted>
                                            </td>
                                            <td>
                                                <b>{r.data?.studentName}</b>
                                                <Muted>
                                                    amoCRM #{amoId(r.data?.amoUrl)}
                                                    {r.data?.quota ? ` · ${formatMessage({ id: 'crmQuota' })}: ${dictLabel(formatMessage, 'Quota', r.data.quota)}` : ''}
                                                </Muted>
                                            </td>
                                            <td>{subjectsLine(r.subjects, locale)}</td>
                                            <td>{dict.label('PACKAGE', r.data?.packageCode)}</td>
                                            <td className="num">
                                                {money(r.data?.paidSum)}
                                                {!!r.data?.discountSum && <Muted>{formatMessage({ id: 'crmDiscountShort' }, { sum: money(r.data.discountSum) })}</Muted>}
                                            </td>
                                            <td>
                                                <Pill tone={st.tone} sx={{ whiteSpace: 'normal' }}>{st.text}</Pill>
                                            </td>
                                            <td className="r">
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={() => navigate(`/crm/register?edit=${r.id}`)}
                                                    sx={{ border: 'none', background: 'transparent', color: ui.pink, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', font: 'inherit' }}
                                                >
                                                    ✎ {formatMessage({ id: 'crmEdit' })}
                                                </Box>
                                            </td>
                                        </tr>
                                    )
                                })}
                                {!lines.length && (
                                    <EmptyRow cols={7} text={formatMessage({ id: 'crmPeriodEmpty' })} />
                                )}
                            </tbody>
                        </table>
                    </TableWrap>
                </Card>
            </Box>
        </Box>
    )
}

export default MyIntakesPage
